import type { Permission } from "../../common";
import {
  type ModeratedSurface,
  ModerationSubjectType,
  type ModerationSurface,
  type ReportCaseReviewer,
} from "./schemas";

/** The surface each kind of item belongs to, which decides its policy, its bans, and who moderates it. */
export const SURFACE_OF_SUBJECT = {
  asset: "assets",
  collection: "assets",
  preset: "presets",
  profile: "profile",
  comment: "social",
} as const satisfies Record<ModerationSubjectType, ModeratedSurface>;

/**
 * Whether items of `subjectType` are labeled only as a whole, file and text together, the way
 * automod judges them at publish, rather than part by part.
 */
export function labeledWhole(subjectType: ModerationSubjectType): boolean {
  return subjectType === "asset" || subjectType === "preset";
}

/** Every moderated surface, in the order the moderation page lists them. */
export const MODERATED_SURFACES = [
  "profile",
  "assets",
  "presets",
  "social",
] as const satisfies readonly ModeratedSurface[];

/** The kinds of item on `surface`. */
export function subjectTypesOf(surface: ModeratedSurface): ModerationSubjectType[] {
  return ModerationSubjectType.options.filter(type => SURFACE_OF_SUBJECT[type] === surface);
}

/**
 * The permissions for each surface: `moderate` works its moderator queue and its appeals and reads
 * users' records, `leadModerate` its lead-moderator queue and reclassifying, and `viewStats` its
 * stats. A lead moderator's role holds the first two.
 */
export const MODERATION_PERMISSIONS = {
  assets: {
    moderate: "assets.moderate",
    leadModerate: "assets.lead_moderate",
    viewStats: "asset_moderation_stats.view",
  },
  presets: {
    moderate: "presets.moderate",
    leadModerate: "presets.lead_moderate",
    viewStats: "preset_moderation_stats.view",
  },
  profile: {
    moderate: "profiles.moderate",
    leadModerate: "profiles.lead_moderate",
    viewStats: "profile_moderation_stats.view",
  },
  social: {
    moderate: "comments.moderate",
    leadModerate: "comments.lead_moderate",
    viewStats: "comment_moderation_stats.view",
  },
} as const satisfies Record<
  ModeratedSurface,
  { moderate: Permission; leadModerate: Permission; viewStats: Permission }
>;

/** The permission that shows each surface's stats, live games included. */
export const STATS_PERMISSIONS = {
  profile: MODERATION_PERMISSIONS.profile.viewStats,
  assets: MODERATION_PERMISSIONS.assets.viewStats,
  presets: MODERATION_PERMISSIONS.presets.viewStats,
  social: MODERATION_PERMISSIONS.social.viewStats,
  live_game: "live_game_moderation_stats.view",
} as const satisfies Record<ModerationSurface, Permission>;

/** The surfaces whose bans can be appealed, and the permission that decides those appeals. */
export const BAN_APPEAL_PERMISSIONS = {
  social: MODERATION_PERMISSIONS.social.moderate,
  live_game: "live_games.moderate",
} as const satisfies Partial<Record<ModerationSurface, Permission>>;
export type BanAppealSurface = keyof typeof BAN_APPEAL_PERMISSIONS;

/** Whether a ban on `surface` can be appealed. */
export const banAppealable = (
  surface: ModerationSurface | "full_site",
): surface is BanAppealSurface => surface in BAN_APPEAL_PERMISSIONS;

/** Every moderation permission. Holding any of them opens the moderation page. */
export const ALL_MODERATION_PERMISSIONS: readonly Permission[] = [
  ...Object.values(MODERATION_PERMISSIONS).flatMap(surface => [
    surface.moderate,
    surface.leadModerate,
    surface.viewStats,
  ]),
  STATS_PERMISSIONS.live_game,
  BAN_APPEAL_PERMISSIONS.live_game,
];

/** A queue people work. */
export type HumanQueue = Exclude<ReportCaseReviewer, "heavy_ai">;

/** The permission that works items of `subjectType` in `queue`. Appeals use the moderator queue's. */
export function queuePermission(subjectType: ModerationSubjectType, queue: HumanQueue): Permission {
  const surface = MODERATION_PERMISSIONS[SURFACE_OF_SUBJECT[subjectType]];
  return queue === "lead_moderator" ? surface.leadModerate : surface.moderate;
}

/** The kinds of item `permissions` let someone work in `queue`. */
export function workableSubjectTypes(
  permissions: readonly Permission[],
  queue: HumanQueue,
): ModerationSubjectType[] {
  return ModerationSubjectType.options.filter(type =>
    permissions.includes(queuePermission(type, queue)),
  );
}

/** What `permissions` let someone do on each surface. */
export function surfaceAccess(permissions: readonly Permission[], surface: ModeratedSurface) {
  const needed = MODERATION_PERMISSIONS[surface];
  return {
    moderate: permissions.includes(needed.moderate),
    leadModerate: permissions.includes(needed.leadModerate),
    viewStats: permissions.includes(needed.viewStats),
  };
}
